(() => {
  document.documentElement.classList.add('sunfade-product-enhancing');
  const desktopLayout = window.matchMedia('(min-width: 990px)');
  const frameSizeRequests = new WeakMap();
  const frameResizeObservers = new WeakMap();

  const icons = {
    description: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 3.75h8l4 4V20.25H6z"/><path d="M14 3.75v4h4M9 12h6M9 15.5h6"/></svg>',
    care: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 20.25S4.5 16 4.5 9.75A4 4 0 0 1 12 7.8a4 4 0 0 1 7.5 1.95C19.5 16 12 20.25 12 20.25Z"/></svg>',
    shipping: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3.75 6.75h10.5v10.5H3.75zM14.25 10.25h3.25l2.75 3v4h-6z"/><circle cx="8" cy="18" r="1.75"/><circle cx="17.5" cy="18" r="1.75"/></svg>',
    location: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 21s6.5-5.65 6.5-12a6.5 6.5 0 1 0-13 0C5.5 15.35 12 21 12 21Z"/><circle cx="12" cy="9" r="2.25"/></svg>',
    personalize: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 3.5v17M7.75 7.25C7.75 5.45 9.55 4 12 4s4.25 1.45 4.25 3.25S14.45 10.5 12 10.5s-4.25 1.45-4.25 3.25S9.55 17 12 17s4.25-1.45 4.25-3.25"/></svg>',
    share: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="18" cy="5" r="2.25"/><circle cx="6" cy="12" r="2.25"/><circle cx="18" cy="19" r="2.25"/><path d="m8 10.9 7.9-4.6M8 13.1l7.9 4.6"/></svg>',
    chevron: '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m4 6 4 4 4-4"/></svg>',
  };

  function initGallery(root = document) {
    root.querySelectorAll('[data-sunfade-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.sunfadeGalleryReady === 'true') return;

      const buttons = [...gallery.querySelectorAll('[data-sunfade-product-thumb]')];
      const media = [...gallery.querySelectorAll('[data-sunfade-product-media]')];
      const mediaList = gallery.querySelector('.sunfade-product-gallery__media');

      if (!buttons.length || !media.length || !mediaList) return;

      gallery.dataset.sunfadeGalleryReady = 'true';

      const setActive = (id) => {
        buttons.forEach((button) => {
          button.setAttribute('aria-current', String(button.dataset.sunfadeProductThumb === id));
        });
      };

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const target = gallery.querySelector(
            `[data-sunfade-product-media="${CSS.escape(button.dataset.sunfadeProductThumb)}"]`,
          );

          if (!target) return;

          if (getComputedStyle(mediaList).overflowY === 'visible') {
            target.scrollIntoView({ behavior: 'instant', block: 'start' });
          } else {
            const targetTop =
              target.getBoundingClientRect().top - mediaList.getBoundingClientRect().top + mediaList.scrollTop;
            mediaList.scrollTop = targetTop;
          }
          setActive(button.dataset.sunfadeProductThumb);
        });
      });

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            const visible = entries
              .filter((entry) => entry.isIntersecting)
              .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

            if (visible) setActive(visible.target.dataset.sunfadeProductMedia);
          },
          {
            root: getComputedStyle(mediaList).overflowY === 'visible' ? null : mediaList,
            rootMargin: '-10% 0px -60% 0px',
            threshold: [0.15, 0.35, 0.6],
          },
        );

        media.forEach((item) => observer.observe(item));
      }
    });
  }

  function productGrids(root = document) {
    const grids = [...root.querySelectorAll('.product-information__grid')];
    if (root.matches?.('.product-information__grid')) grids.unshift(root);
    return grids;
  }

  function markProductEnhancementReady(grid) {
    grid.dataset.sunfadeProductReady = 'true';

    const hasUnprocessedDescription = productGrids(document).some((candidate) => {
      const details = candidate.querySelector('[data-testid="product-information-details"]');
      const content = details?.querySelector(':scope > .group-block > .group-block-content');
      return Boolean(content?.querySelector(':scope > rte-formatter:not([data-sunfade-description])'));
    });

    if (!hasUnprocessedDescription) {
      document.documentElement.classList.add('sunfade-product-ready');
      document.documentElement.classList.remove('sunfade-product-pending');
      document.documentElement.classList.remove('sunfade-product-enhancing');
    }
  }

  function collapsedAccordionHeight(accordionGroup) {
    if (!accordionGroup) return 0;

    const groupStyle = getComputedStyle(accordionGroup);
    const borderHeight =
      (Number.parseFloat(groupStyle.borderBlockStartWidth) || 0) +
      (Number.parseFloat(groupStyle.borderBlockEndWidth) || 0);
    const accordionHeight = [...accordionGroup.querySelectorAll(':scope > .sunfade-product-accordion')].reduce(
      (height, accordion) => {
        const accordionStyle = getComputedStyle(accordion);
        const summary = accordion.querySelector(':scope > summary');
        const summaryHeight = summary?.getBoundingClientRect().height || 0;
        const accordionBorders =
          (Number.parseFloat(accordionStyle.borderBlockStartWidth) || 0) +
          (Number.parseFloat(accordionStyle.borderBlockEndWidth) || 0);

        return height + summaryHeight + accordionBorders;
      },
      0,
    );
    const shareHeight =
      accordionGroup.querySelector(':scope > .sunfade-product-share')?.getBoundingClientRect().height || 0;

    return borderHeight + accordionHeight + shareHeight;
  }

  function collapsedDetailsHeight(detailsColumn) {
    const group = detailsColumn?.querySelector(':scope > .group-block');
    const content = group?.querySelector(':scope > .group-block-content');
    if (!group || !content) return 0;

    const children = [...content.children].filter((child) => getComputedStyle(child).display !== 'none');
    const contentStyle = getComputedStyle(content);
    const groupStyle = getComputedStyle(group);
    const gap = Number.parseFloat(contentStyle.rowGap) || 0;
    const childrenHeight = children.reduce((height, child) => {
      const childHeight = child.matches('.sunfade-product-accordions')
        ? collapsedAccordionHeight(child)
        : child.getBoundingClientRect().height;

      return height + childHeight;
    }, 0);
    const groupChrome =
      (Number.parseFloat(groupStyle.paddingBlockStart) || 0) +
      (Number.parseFloat(groupStyle.paddingBlockEnd) || 0) +
      (Number.parseFloat(groupStyle.borderBlockStartWidth) || 0) +
      (Number.parseFloat(groupStyle.borderBlockEndWidth) || 0);

    return Math.ceil(childrenHeight + gap * Math.max(children.length - 1, 0) + groupChrome);
  }

  function productColumnsAreSideBySide(grid, mediaColumn, detailsColumn) {
    if (!desktopLayout.matches || !mediaColumn || !detailsColumn) return false;

    const mediaRect = mediaColumn.getBoundingClientRect();
    const detailsRect = detailsColumn.getBoundingClientRect();

    return Math.abs(mediaRect.top - detailsRect.top) <= 2 && Math.abs(mediaRect.left - detailsRect.left) > 8;
  }

  function setProductFrameHeight(grid) {
    const mediaColumn = grid.querySelector('.product-information__media');
    const detailsColumn = grid.querySelector('[data-testid="product-information-details"]');
    const firstMedia = grid.querySelector('[data-sunfade-product-media]');

    grid.style.removeProperty('--sunfade-product-initial-height');
    grid.removeAttribute('data-sunfade-scroll-frame');

    if (!productColumnsAreSideBySide(grid, mediaColumn, detailsColumn) || !firstMedia) {
      markProductEnhancementReady(grid);
      return;
    }

    // Measure the stable collapsed controls, not the currently expanded accordion.
    // Expanded details are allowed to grow the grid naturally instead of overflowing
    // a fixed-height product column into the following Why Love section.
    const collapsedHeight = collapsedDetailsHeight(detailsColumn);
    const detailsHeight = Math.ceil(collapsedHeight || detailsColumn.scrollHeight || 0);
    const firstMediaHeight = Math.ceil(firstMedia.getBoundingClientRect().height || firstMedia.scrollHeight || 0);
    const balancedHeight = Math.max(detailsHeight, firstMediaHeight);

    if (!balancedHeight) return;

    grid.style.setProperty('--sunfade-product-initial-height', `${balancedHeight}px`);
    grid.dataset.sunfadeScrollFrame = 'true';
    markProductEnhancementReady(grid);
  }

  function scheduleFrameSize(grid) {
    // Coalesce to the next frame without canceling it. App widgets can emit a
    // continuous stream of mutations; cancel-and-reschedule starves measurement.
    if (frameSizeRequests.has(grid)) return;

    frameSizeRequests.set(
      grid,
      requestAnimationFrame(() => {
        frameSizeRequests.delete(grid);
        setProductFrameHeight(grid);
      }),
    );
  }

  function observeProductFrame(grid) {
    if (!('ResizeObserver' in window)) return;

    const detailsColumn = grid.querySelector('[data-testid="product-information-details"]');
    const content = detailsColumn?.querySelector(':scope > .group-block > .group-block-content');
    const firstMedia = grid.querySelector('[data-sunfade-product-media]');
    if (!content || !firstMedia) return;

    const previous = frameResizeObservers.get(grid);
    if (previous?.content === content && previous?.firstMedia === firstMedia) return;
    previous?.observer.disconnect();

    let lastContentHeight = -1;
    let lastMediaHeight = -1;
    const observer = new ResizeObserver(() => {
      const contentHeight = Math.round(content.getBoundingClientRect().height);
      const mediaHeight = Math.round(firstMedia.getBoundingClientRect().height);
      if (contentHeight === lastContentHeight && mediaHeight === lastMediaHeight) return;

      lastContentHeight = contentHeight;
      lastMediaHeight = mediaHeight;
      scheduleFrameSize(grid);
    });

    observer.observe(content);
    observer.observe(firstMedia);
    frameResizeObservers.set(grid, { observer, content, firstMedia });
  }

  function arrangePurchaseStack(groupContent) {
    const formButtons = groupContent.querySelector('.product-form-buttons');
    if (!formButtons || formButtons.dataset.sunfadePurchaseReady === 'true') return;

    const quantity = formButtons.querySelector('.quantity-selector-wrapper');
    const addButton = formButtons.querySelector('.add-to-cart-button');
    const addRow = [...formButtons.children].find((child) => child.contains(addButton));
    const eddPanel = [...groupContent.children].find((child) => child.querySelector?.('[data-edd-widget]'));
    const sourcePriceRoot = groupContent.querySelector('product-price');

    if (!quantity || !addButton || !addRow || !eddPanel || !sourcePriceRoot) return;

    formButtons.dataset.sunfadePurchaseReady = 'true';
    formButtons.classList.add('sunfade-purchase-stack');

    const quantityLine = document.createElement('div');
    quantityLine.className = 'sunfade-quantity-line';

    const quantityControl = document.createElement('div');
    quantityControl.className = 'sunfade-quantity-control';

    const quantityLabel = document.createElement('span');
    quantityLabel.className = 'sunfade-quantity-label';
    quantityLabel.textContent = 'Quantity:';

    const quantityPrice = document.createElement('div');
    quantityPrice.className = 'sunfade-quantity-price';
    quantityPrice.setAttribute('aria-live', 'polite');

    const quantityPriceAmount = document.createElement('span');
    quantityPriceAmount.className = 'sunfade-quantity-price__amount';

    const quantityPriceUnit = document.createElement('span');
    quantityPriceUnit.className = 'sunfade-quantity-price__unit';
    quantityPriceUnit.textContent = 'each';

    const quantityInput = quantity.querySelector('input[type="number"]');
    const minusButton = quantity.querySelector('[name="minus"]');
    const plusButton = quantity.querySelector('[name="plus"]');

    const updateQuantityButtons = () => {
      if (!quantityInput) return;

      const value = Number(quantityInput.value) || 1;
      const min = Number(quantityInput.min || quantityInput.dataset.min) || 1;
      const max = Number(quantityInput.max) || Number.POSITIVE_INFINITY;

      if (minusButton) minusButton.disabled = value <= min;
      if (plusButton) plusButton.disabled = value >= max;
    };

    const updatePrice = () => {
      const sourcePrice = sourcePriceRoot.querySelector('.price');
      if (!sourcePrice) return;
      quantityPriceAmount.textContent = sourcePrice.textContent.trim();
    };

    updatePrice();
    updateQuantityButtons();
    quantityControl.append(quantityLabel, quantity);
    quantityPrice.append(quantityPriceAmount, quantityPriceUnit);
    quantityLine.append(quantityControl, quantityPrice);
    formButtons.prepend(quantityLine);

    eddPanel.classList.add('sunfade-edd-order-panel');
    addRow.classList.add('sunfade-add-to-cart-row');
    formButtons.insertBefore(eddPanel, addRow);

    quantity.addEventListener('click', () => {
      requestAnimationFrame(updateQuantityButtons);
      window.setTimeout(updateQuantityButtons, 120);
    });
    quantityInput?.addEventListener('input', updateQuantityButtons);
    quantityInput?.addEventListener('change', updateQuantityButtons);

    new MutationObserver(updatePrice).observe(sourcePriceRoot, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  function makeTextContent(text, linkLabel, linkHref) {
    const fragment = document.createDocumentFragment();
    const paragraph = document.createElement('p');
    paragraph.textContent = text;
    fragment.append(paragraph);

    if (linkLabel && linkHref) {
      const linkParagraph = document.createElement('p');
      const link = document.createElement('a');
      link.href = linkHref;
      link.textContent = linkLabel;
      linkParagraph.append(link);
      fragment.append(linkParagraph);
    }

    return fragment;
  }

  function addShippingDays(date, days) {
    // Count business days (Mon–Fri) to match the Shipping Policy wording.
    const result = new Date(date);
    result.setHours(12, 0, 0, 0);
    let remaining = days;
    while (remaining > 0) {
      result.setDate(result.getDate() + 1);
      const weekday = result.getDay();
      if (weekday !== 0 && weekday !== 6) remaining -= 1;
    }
    return result;
  }

  function shippingDateLabel(date) {
    return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' }).format(date);
  }

  function shippingDateRange(start, end) {
    const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
    if (sameMonth) {
      const month = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(start);
      return `${month} ${start.getDate()}–${end.getDate()}`;
    }

    return `${shippingDateLabel(start)} – ${shippingDateLabel(end)}`;
  }

  function makeShippingContent() {
    const today = new Date();
    const shipping = document.createElement('div');
    shipping.className = 'shipping sunfade-shipping';
    shipping.innerHTML = `
      <div class="wrapper-ship" hidden>
        <label for="option-ship" class="ship-label-op">Standard Shipping</label>
        <div class="pill-toggle-group" id="option-ship">
          <div class="pill-slider"></div>
          <button type="button" class="pill-btn text-label-s-medium active" data-value="standard">Standard Shipping</button>
        </div>
      </div>
      <div class="wt-grid fulfillment" aria-label="Standard shipping timeline">
        <div class="fulfillment-row fulfillment-row--heading">
          <div class="timeline-title ship-title-display text-label-s-medium">Standard Shipping:</div>
        </div>
        <div class="fulfillment-row">
          <div class="timeline-title text-body-s-medium">Order Placed:</div>
          <div class="timeline-date timeline-current text-body-s-bold" data-shipping-date="placed"></div>
        </div>
        <div class="fulfillment-row">
          <div class="timeline-title text-body-s-medium">Order Ships:</div>
          <div class="timeline-date timeline-ship text-body-s-bold" data-shipping-date="ships"></div>
        </div>
        <div class="fulfillment-row">
          <div class="timeline-title text-body-s-medium">Delivered:</div>
          <div class="timeline-date timeline-delivered text-body-s-bold" data-shipping-date="delivered"></div>
        </div>
      </div>
      <ul class="sunfade-shipping__notes">
        <li>Learn more about our Shipping Policy <a target="_blank" href="/policies/shipping-policy" rel="noopener noreferrer">here</a>.</li>
      </ul>
    `;

    shipping.querySelector('[data-shipping-date="placed"]').textContent = shippingDateLabel(today);
    shipping.querySelector('[data-shipping-date="ships"]').textContent = shippingDateRange(
      addShippingDays(today, 7),
      addShippingDays(today, 10),
    );
    shipping.querySelector('[data-shipping-date="delivered"]').textContent = shippingDateRange(
      addShippingDays(today, 11),
      addShippingDays(today, 17),
    );

    return shipping;
  }
  function makeSoutheastAsiaContent() {
    const content = document.createElement('div');
    content.className = 'sunfade-accordion-copy sunfade-ship-origin';
    content.innerHTML = `
      <p>Your order will be <strong>shipped from SOUTHEAST ASIA</strong> to your delivery address via express international couriers (UPS, DHL, or FedEx).</p>
      <p>This allows us to maintain full control over craftsmanship quality and ensure your item is delivered safely.</p>
      <p>We will send you:</p>
      <ul>
        <li>Raw version preview</li>
        <li>Surface finish preview</li>
        <li>Color preview</li>
        <li>Final finished photos</li>
      </ul>
      <p class="sunfade-ship-origin__confirmation">…before packaging your order.<br>Once confirmed, your item will be shipped immediately.</p>
    `;
    return content;
  }

  function makeAssemblyCareContent() {
    const content = document.createElement('div');
    content.className = 'sunfade-accordion-copy sunfade-assembly-care';
    content.innerHTML = `
      <h4>Assembly</h4>
      <p>Some Juniper Mountain pieces may arrive disassembled for safe delivery. When assembly is required, product-specific guidance will be provided to help with installation.</p>
      <p>If you need installation assistance, contact us at <a href="mailto:support@junipersmountain.com">support@junipersmountain.com</a>.</p>
      <h4>Care &amp; Maintenance</h4>
      <ul>
        <li><strong>Environment:</strong> Keep in a stable temperature and humidity. Avoid direct sunlight and damp areas.</li>
        <li><strong>Moisture Control:</strong> Use a humidifier in dry conditions and a dehumidifier in humid areas.</li>
        <li><strong>Protection:</strong> Avoid water, heat, and steam exposure. Wipe spills immediately.</li>
        <li><strong>Maintenance:</strong> Apply wood oil or beeswax 3–4 times yearly. Check and tighten joints if needed.</li>
        <li><strong>Cleaning:</strong> Dust with a soft cloth. Apply beeswax along the grain and buff dry.</li>
        <li><strong>Safety:</strong> Avoid eye or mouth contact with care products. Keep them away from children, test before full use, and store in a cool, dry place.</li>
      </ul>
    `;
    return content;
  }

  function makePersonalizeContent() {
    const content = document.createElement('div');
    content.className = 'sunfade-accordion-copy sunfade-personalize-process';
    content.innerHTML = `
      <ol>
        <li class="sunfade-personalize-process__step">
          <strong>Step 1: Receiving requests from customer</strong>
          <p>Provide the dimensions, design, materials, and colors you want.</p>
        </li>
        <li class="sunfade-personalize-process__step">
          <strong>Step 2: Production</strong>
          <p>Juniper Mountain will proceed with product manufacturing after the customization details are confirmed in writing. The production timeline will be confirmed based on the product’s complexity.</p>
        </li>
        <li class="sunfade-personalize-process__step">
          <strong>Step 3: First check</strong>
          <p>We send a preliminary inspection through available images and videos.</p>
        </li>
        <li class="sunfade-personalize-process__step">
          <strong>Step 4: Finishing touches</strong>
          <p>We make adjustments and finalize the product according to your confirmed feedback.</p>
        </li>
        <li class="sunfade-personalize-process__step">
          <strong>Step 5: Final check</strong>
          <p>We send available images and videos of the completed product for inspection.</p>
        </li>
        <li class="sunfade-personalize-process__step">
          <strong>Step 6: Packing and shipping</strong>
          <p>The product is packaged and shipped. The delivery estimate will be confirmed based on the destination and selected service.</p>
        </li>
      </ol>
      <p class="sunfade-personalize-process__contact">Contact <a href="mailto:support@junipersmountain.com">support@junipersmountain.com</a> before ordering to confirm whether personalization is available for your item.</p>
    `;
    return content;
  }
  function makeAccordion(label, iconName, content) {
    const details = document.createElement('details');
    details.className = 'sunfade-product-accordion';
    details.dataset.sunfadeProductAccordion = '';

    const summary = document.createElement('summary');
    summary.innerHTML = `
      <span class="sunfade-product-accordion__icon">${icons[iconName]}</span>
      <span>${label}</span>
      <span class="sunfade-product-accordion__toggle">${icons.chevron}</span>
    `;

    const panel = document.createElement('div');
    panel.className = 'sunfade-product-accordion__content';
    panel.append(content);

    details.append(summary, panel);
    return details;
  }

  async function shareProduct(button) {
    const label = button.querySelector('.sunfade-product-share__label');
    const shareData = {
      title: document.title,
      text: document.querySelector('.product-details h1, .product-details h2, .product-details h3')?.textContent?.trim(),
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareData.url);
      } else {
        const input = document.createElement('textarea');
        input.value = shareData.url;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.append(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }

      label.textContent = 'LINK COPIED';
      window.setTimeout(() => {
        label.textContent = 'Share';
      }, 1800);
    } catch (error) {
      if (error?.name !== 'AbortError') label.textContent = 'Share';
    }
  }

  function makeStoryImage(source, className) {
    const image = document.createElement('img');
    image.className = className;
    image.src = source.currentSrc || source.src;
    image.alt = source.alt || '';
    image.loading = 'lazy';
    image.decoding = 'async';

    if (source.srcset) image.srcset = source.srcset;
    if (source.sizes) image.sizes = source.sizes;

    return image;
  }

  function initProductStory(root = document) {
    productGrids(root).forEach((grid) => {
      const productInformation = grid.closest('.product-information');
      const host = productInformation?.parentElement;
      const description = grid.querySelector('[data-sunfade-description]');
      const sourceImages = [...grid.querySelectorAll('[data-sunfade-product-media] img')].filter(
        (image) => image.currentSrc || image.src,
      );

      if (!host || !productInformation || !description || !sourceImages.length) return;
      if (host.querySelector(':scope > [data-sunfade-product-story]')) return;

      const story = document.createElement('section');
      story.className = 'sunfade-product-story';
      story.dataset.sunfadeProductStory = '';
      story.setAttribute('aria-labelledby', 'sunfade-product-story-title');

      const title = document.createElement('h2');
      title.id = 'sunfade-product-story-title';
      title.className = 'sunfade-product-story__title';
      title.textContent = "Why You'll Love It";

      const shell = document.createElement('div');
      shell.className = 'sunfade-product-story__shell';

      const hero = document.createElement('figure');
      hero.className = 'sunfade-product-story__hero';
      hero.append(makeStoryImage(sourceImages.at(-1), 'sunfade-product-story__hero-image'));

      const fade = document.createElement('div');
      fade.className = 'sunfade-product-story__fade';
      fade.setAttribute('aria-hidden', 'true');

      const contentId = 'sunfade-product-story-content';
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'sunfade-product-story__toggle sunfade-product-story__more';
      more.textContent = 'See more';
      more.setAttribute('aria-controls', contentId);
      more.setAttribute('aria-expanded', 'false');

      const details = document.createElement('div');
      details.id = contentId;
      details.className = 'sunfade-product-story__details';
      details.hidden = true;

      const intro = document.createElement('div');
      intro.className = 'sunfade-product-story__intro';
      intro.dataset.sunfadeStoryReveal = '';
      intro.innerHTML = description.innerHTML;
      details.append(intro);

      const visualSources = sourceImages.length > 1 ? sourceImages.slice(0, -1) : [];
      if (visualSources.length) {
        const visuals = document.createElement('div');
        visuals.className = 'sunfade-product-story__visuals';

        visualSources.forEach((source) => {
          const visual = document.createElement('figure');
          visual.className = 'sunfade-product-story__visual';
          visual.dataset.sunfadeStoryReveal = '';
          visual.append(makeStoryImage(source, 'sunfade-product-story__visual-image'));
          visuals.append(visual);
        });

        details.append(visuals);
      }

      const lessRow = document.createElement('div');
      lessRow.className = 'sunfade-product-story__less-row';

      const less = document.createElement('button');
      less.type = 'button';
      less.className = 'sunfade-product-story__toggle sunfade-product-story__less';
      less.textContent = 'See less';
      less.setAttribute('aria-controls', contentId);
      lessRow.append(less);
      details.append(lessRow);

      shell.append(hero, fade, more, details);
      story.append(title, shell);
      productInformation.insertAdjacentElement('afterend', story);

      let revealObserver;
      let revealFallbackCleanup;
      let revealFrame;
      let revealPoll;
      const revealTargets = [...details.querySelectorAll('[data-sunfade-story-reveal]')];

      const stopRevealTracking = () => {
        revealObserver?.disconnect();
        revealObserver = undefined;
        revealFallbackCleanup?.();
        revealFallbackCleanup = undefined;
        if (revealFrame) window.cancelAnimationFrame(revealFrame);
        revealFrame = undefined;
        if (revealPoll) window.clearInterval(revealPoll);
        revealPoll = undefined;
      };

      const revealTarget = (target) => {
        if (!story.classList.contains('is-expanded')) return;
        target.classList.add('is-visible');
      };

      const startReveals = () => {
        stopRevealTracking();

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          revealTargets.forEach((target) => target.classList.add('is-visible'));
          return;
        }

        story.classList.add('sunfade-product-story--motion-ready');
        void story.offsetWidth;

        if ('IntersectionObserver' in window) {
          revealObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                revealTarget(entry.target);
                revealObserver?.unobserve(entry.target);
              });
            },
            { rootMargin: '0px 0px -18% 0px', threshold: 0.08 },
          );
          revealTargets.forEach((target) => revealObserver.observe(target));
        }

        const revealInViewport = () => {
          revealFrame = undefined;
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

          revealTargets.forEach((target) => {
            if (target.classList.contains('is-visible')) return;
            const rect = target.getBoundingClientRect();
            if (rect.top <= viewportHeight * 0.82 && rect.bottom >= viewportHeight * 0.08) {
              revealTarget(target);
            }
          });

          if (revealTargets.every((target) => target.classList.contains('is-visible'))) {
            revealObserver?.disconnect();
            revealObserver = undefined;
            revealFallbackCleanup?.();
            revealFallbackCleanup = undefined;
            if (revealPoll) window.clearInterval(revealPoll);
            revealPoll = undefined;
          }
        };

        const queueRevealCheck = () => {
          if (revealFrame) return;
          revealFrame = window.requestAnimationFrame(revealInViewport);
        };

        window.addEventListener('scroll', queueRevealCheck, { passive: true });
        window.addEventListener('resize', queueRevealCheck);
        revealPoll = window.setInterval(queueRevealCheck, 180);
        revealFallbackCleanup = () => {
          window.removeEventListener('scroll', queueRevealCheck);
          window.removeEventListener('resize', queueRevealCheck);
          if (revealPoll) window.clearInterval(revealPoll);
          revealPoll = undefined;
        };
        queueRevealCheck();
      };

      more.addEventListener('click', () => {
        details.hidden = false;
        more.hidden = true;
        more.setAttribute('aria-expanded', 'true');
        less.setAttribute('aria-expanded', 'true');
        story.classList.add('is-expanded');
        requestAnimationFrame(startReveals);
      });

      less.addEventListener('click', () => {
        stopRevealTracking();
        story.classList.remove('is-expanded', 'sunfade-product-story--motion-ready');
        revealTargets.forEach((target) => target.classList.remove('is-visible'));
        details.hidden = true;
        more.hidden = false;
        more.setAttribute('aria-expanded', 'false');
        less.setAttribute('aria-expanded', 'false');
        story.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.setTimeout(() => more.focus({ preventScroll: true }), 350);
      });
    });
  }

  function initProductInformation(root = document) {
    productGrids(root).forEach((grid) => {
      const detailsColumn = grid.querySelector('[data-testid="product-information-details"]');
      const groupContent = detailsColumn?.querySelector(':scope > .group-block > .group-block-content');
      const gallery = grid.querySelector('[data-sunfade-product-gallery]');

      if (!detailsColumn || !groupContent || !gallery) return;

      observeProductFrame(grid);
      arrangePurchaseStack(groupContent);

      if (groupContent.dataset.sunfadeAccordionsReady === 'true') {
        setProductFrameHeight(grid);
        scheduleFrameSize(grid);
        return;
      }

      const productDescription = groupContent.querySelector(':scope > rte-formatter');
      if (!productDescription) return;

      groupContent.dataset.sunfadeAccordionsReady = 'true';
      const descriptionText = productDescription.textContent.trim();
      productDescription.dataset.sunfadeDescription = '';

      const accordionGroup = document.createElement('div');
      accordionGroup.className = 'sunfade-product-accordions';
      accordionGroup.dataset.sunfadeProductAccordions = '';

      if (!descriptionText) {
        productDescription.append(makeTextContent('Product details are not currently listed for this item.'));
      }

      const description = makeAccordion('Description', 'description', productDescription);
      const shipOrigin = makeAccordion(
        'Ship From Southeast Asia',
        'location',
        makeSoutheastAsiaContent(),
      );
      const care = makeAccordion(
        'Assembly & Care',
        'care',
        makeAssemblyCareContent(),
      );
      const shipping = makeAccordion(
        'Shipping & Returns',
        'shipping',
        makeShippingContent(),
      );
      const personalize = makeAccordion(
        'Personalize',
        'personalize',
        makePersonalizeContent(),
      );

      accordionGroup.append(description, shipOrigin, care, shipping, personalize);

      const share = document.createElement('button');
      share.type = 'button';
      share.className = 'sunfade-product-share';
      share.innerHTML = `
        <span class="sunfade-product-share__icon">${icons.share}</span>
        <span class="sunfade-product-share__label">Share</span>
      `;
      share.addEventListener('click', () => shareProduct(share));
      accordionGroup.append(share);
      groupContent.append(accordionGroup);

      // Reserve the finished product-row height in the same task that creates
      // the accordions, before the browser can paint the following section.
      setProductFrameHeight(grid);

      const observer = new MutationObserver(() => scheduleFrameSize(grid));
      observer.observe(groupContent, { childList: true, characterData: true, subtree: true });
      gallery.querySelectorAll('img').forEach((image) => {
        if (!image.complete) image.addEventListener('load', () => scheduleFrameSize(grid), { once: true });
      });

      scheduleFrameSize(grid);
    });
  }

  const finishDetails = {
    'smooth surface': {
      title: 'Smooth Surface',
      badge: 'Gloss',
      subtitle: 'Sleek & Easy to Clean',
      image: 'https://cdn.shopify.com/s/files/1/0749/0453/5178/files/juniper-finish-smooth-surface.png?v=1787022616',
      process: ['Fine-sanded', 'PU base coat', 'PU gloss topcoat'],
      bullets: [
        'A polished, satin-smooth top with a soft sheen.',
        'Sealed with a protective gloss layer for durability, moisture resistance, and effortless daily care.',
        'Perfect for modern, contemporary interiors.',
      ],
      care: 'Wipe with a soft, dry cloth. Avoid harsh chemicals.',
    },
    'natural rough surface': {
      title: 'Natural Rough Surface',
      badge: 'Matte',
      subtitle: 'Authentic Wood Texture',
      image: 'https://cdn.shopify.com/s/files/1/0749/0453/5178/files/juniper-finish-natural-rough-surface.png?v=1787022606',
      process: ['Steel wire-brushed', 'Single PU coat', 'Matte finish'],
      bullets: [
        "A flat yet tactile surface that highlights the wood's natural grain, knots, and pores.",
        'Sealed with a matte PU coat to preserve its raw, hand-touched character.',
        'Ideal for rustic, farmhouse, and mid-century styles.',
      ],
      care: 'Dust regularly with a soft, dry cloth. Avoid wet cleaning.',
    },
  };

  const finishMobile = window.matchMedia('(max-width: 989px)');

  function finishConfig(value) {
    return finishDetails[String(value || '').trim().toLowerCase()];
  }

  function makeFinishProcessSteps(container, config, prefix) {
    container.replaceChildren();
    config.process.forEach((step, index) => {
      if (index) {
        const arrow = document.createElement('span');
        arrow.className = `${prefix}-arrow`;
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = '→';
        container.append(arrow);
      }

      const chip = document.createElement('span');
      chip.className = `${prefix}-chip`;
      chip.textContent = step;
      container.append(chip);
    });
  }

  function fillFinishInfo(container, config, prefix) {
    container.querySelector(`.${prefix}-title`).textContent = config.title;
    container.querySelector(`.${prefix}-subtitle`).textContent = config.subtitle;
    makeFinishProcessSteps(container.querySelector(`.${prefix}-steps`), config, prefix);

    const list = container.querySelector(`.${prefix}-list`);
    list.replaceChildren();
    config.bullets.forEach((copy) => {
      const item = document.createElement('li');
      item.textContent = copy;
      list.append(item);
    });

    container.querySelector(`.${prefix}-care-text`).textContent = config.care;
  }

  function finishInfoMarkup(prefix) {
    return `
      <strong class="${prefix}-title"></strong>
      <p class="${prefix}-subtitle"></p>
      <div class="${prefix}-process">
        <span class="${prefix}-process-label">Finish process</span>
        <div class="${prefix}-steps"></div>
      </div>
      <ul class="${prefix}-list"></ul>
      <div class="${prefix}-care">
        <span class="${prefix}-care-label">Care</span>
        <span class="${prefix}-care-text"></span>
      </div>
    `;
  }

  function initFinishPicker(fieldset) {
    if (fieldset.dataset.sunfadeFinishReady === 'true') return;

    const labels = [...fieldset.querySelectorAll(':scope > label.variant-option__button-label')];
    const options = labels.map((label) => {
      const input = label.querySelector('input[type="radio"]');
      return { label, input, config: finishConfig(input?.value) };
    });

    if (options.length !== 2 || options.some((option) => !option.input || !option.config)) return;

    fieldset.dataset.sunfadeFinishReady = 'true';
    fieldset.classList.add('sunfade-finish-picker');

    const owner = fieldset.dataset.optionId || `sunfade-finish-${Math.random().toString(36).slice(2)}`;
    document
      .querySelectorAll('[data-sunfade-finish-overlay]')
      .forEach((overlay) => overlay.dataset.sunfadeFinishOverlay === owner && overlay.remove());

    const legend = fieldset.querySelector(':scope > legend');
    const legendLabel = document.createElement('span');
    legendLabel.className = 'sunfade-finish-picker__legend-label';
    legendLabel.textContent = 'Finish:';

    const selectedLabel = document.createElement('span');
    selectedLabel.className = 'sunfade-finish-picker__selected';

    const infoButton = document.createElement('button');
    infoButton.type = 'button';
    infoButton.className = 'sunfade-finish-picker__info';
    infoButton.setAttribute('aria-label', 'View finish information');
    infoButton.textContent = '?';

    legend.replaceChildren(legendLabel, selectedLabel, infoButton);

    options.forEach(({ label, input, config }) => {
      label.classList.add('sunfade-finish-card');
      [...label.children].forEach((child) => {
        if (child !== input) child.remove();
      });

      const media = document.createElement('span');
      media.className = 'sunfade-finish-card__media';

      const image = document.createElement('img');
      image.src = config.image;
      image.alt = `${config.title} wood texture`;
      image.loading = 'lazy';
      image.decoding = 'async';
      media.append(image);

      const nameRow = document.createElement('span');
      nameRow.className = 'sunfade-finish-card__name-row';

      const name = document.createElement('span');
      name.className = 'sunfade-finish-card__name';
      name.textContent = config.title;

      const badge = document.createElement('span');
      badge.className = 'sunfade-finish-card__badge';
      badge.textContent = config.badge;

      nameRow.append(name, badge);
      label.append(media, nameRow);
    });

    const popup = document.createElement('div');
    popup.className = 'sunfade-finish-popup';
    popup.dataset.sunfadeFinishOverlay = owner;
    popup.setAttribute('aria-hidden', 'true');
    popup.innerHTML = finishInfoMarkup('sunfade-finish-popup');
    document.body.append(popup);

    const drawer = document.createElement('div');
    drawer.className = 'sunfade-finish-drawer';
    drawer.dataset.sunfadeFinishOverlay = owner;
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.innerHTML = `
      <button type="button" class="sunfade-finish-drawer__backdrop" aria-label="Close finish information"></button>
      <div class="sunfade-finish-drawer__panel">
        <div class="sunfade-finish-drawer__header">
          <strong>Finish</strong>
          <button type="button" class="sunfade-finish-drawer__close" aria-label="Close finish information">×</button>
        </div>
        <div class="sunfade-finish-drawer__cards"></div>
        <div class="sunfade-finish-drawer__info">
          ${finishInfoMarkup('sunfade-finish-drawer')}
        </div>
        <button type="button" class="sunfade-finish-drawer__select">Select finish</button>
      </div>
    `;
    document.body.append(drawer);

    const drawerCards = drawer.querySelector('.sunfade-finish-drawer__cards');
    let pendingInput;
    let previouslyFocused;

    options.forEach(({ input, config }) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sunfade-finish-drawer__card';
      button.dataset.finishValue = input.value;
      button.innerHTML = `
        <span class="sunfade-finish-drawer__card-media">
          <img src="${config.image}" alt="${config.title} wood texture" loading="lazy">
        </span>
        <span class="sunfade-finish-drawer__card-name">${config.title}</span>
      `;
      drawerCards.append(button);
    });

    const updateSelected = () => {
      const checked = options.find(({ input }) => input.checked) || options[0];
      selectedLabel.textContent = checked.config.title;
    };

    const positionPopup = () => {
      const firstRect = options[0].label.getBoundingClientRect();
      const lastRect = options.at(-1).label.getBoundingClientRect();
      const width = Math.min(680, Math.max(360, lastRect.right - firstRect.left));
      popup.style.width = `${width}px`;
      popup.style.left = `${Math.min(Math.max(8, firstRect.left), window.innerWidth - width - 8)}px`;
      popup.style.top = '0px';

      const popupHeight = popup.offsetHeight;
      const cardsBottom = Math.max(...options.map(({ label }) => label.getBoundingClientRect().bottom));
      const cardsTop = Math.min(...options.map(({ label }) => label.getBoundingClientRect().top));
      const showAbove = window.innerHeight - cardsBottom < popupHeight + 14 && cardsTop > popupHeight + 14;
      popup.classList.toggle('sunfade-finish-popup--above', showAbove);
      popup.style.top = `${showAbove ? cardsTop - popupHeight - 10 : cardsBottom + 10}px`;
    };

    let popupHideTimer;
    let activePopupOption;

    const showPopup = (option) => {
      if (finishMobile.matches) return;
      window.clearTimeout(popupHideTimer);
      activePopupOption = option;
      fillFinishInfo(popup, option.config, 'sunfade-finish-popup');
      popup.classList.add('is-visible');
      popup.setAttribute('aria-hidden', 'false');
      positionPopup();
    };

    const hidePopup = () => {
      popupHideTimer = window.setTimeout(() => {
        popup.classList.remove('is-visible');
        popup.setAttribute('aria-hidden', 'true');
        activePopupOption = undefined;
      }, 80);
    };

    const showDrawerOption = (option) => {
      pendingInput = option.input;
      fillFinishInfo(drawer, option.config, 'sunfade-finish-drawer');
      drawer.querySelectorAll('.sunfade-finish-drawer__card').forEach((card) => {
        card.classList.toggle('is-selected', card.dataset.finishValue === option.input.value);
      });
    };

    const closeDrawer = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('sunfade-finish-drawer-open');
      previouslyFocused?.focus?.({ preventScroll: true });
    };

    const openDrawer = (option) => {
      if (!finishMobile.matches) return;
      previouslyFocused = document.activeElement;
      showDrawerOption(option);
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('sunfade-finish-drawer-open');
      drawer.querySelector('.sunfade-finish-drawer__close').focus({ preventScroll: true });
    };

    options.forEach((option) => {
      option.input.addEventListener('change', updateSelected);
      option.label.addEventListener('mouseenter', () => showPopup(option));
      option.label.addEventListener('mouseleave', hidePopup);
      option.label.addEventListener('focusin', () => showPopup(option));
      option.label.addEventListener('focusout', hidePopup);
    });

    popup.addEventListener('mouseenter', () => window.clearTimeout(popupHideTimer));
    popup.addEventListener('mouseleave', hidePopup);
    window.addEventListener('scroll', () => activePopupOption && positionPopup(), { passive: true });
    window.addEventListener('resize', () => activePopupOption && positionPopup());

    drawerCards.querySelectorAll('.sunfade-finish-drawer__card').forEach((card) => {
      card.addEventListener('click', () => {
        const option = options.find(({ input }) => input.value === card.dataset.finishValue);
        if (option) showDrawerOption(option);
      });
    });

    infoButton.addEventListener('click', (event) => {
      event.preventDefault();
      const checked = options.find(({ input }) => input.checked) || options[0];
      openDrawer(checked);
    });
    drawer.querySelector('.sunfade-finish-drawer__close').addEventListener('click', closeDrawer);
    drawer.querySelector('.sunfade-finish-drawer__backdrop').addEventListener('click', closeDrawer);
    drawer.querySelector('.sunfade-finish-drawer__select').addEventListener('click', () => {
      pendingInput?.click();
      closeDrawer();
    });
    drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeDrawer();
    });
    finishMobile.addEventListener?.('change', () => {
      popup.classList.remove('is-visible');
      popup.setAttribute('aria-hidden', 'true');
      if (!finishMobile.matches) closeDrawer();
    });

    updateSelected();
  }

  function initFinishOptions(root = document) {
    root.querySelectorAll?.('fieldset.variant-option').forEach((fieldset) => {
      const optionId = fieldset.dataset.optionId?.toLowerCase() || '';
      const legendText = fieldset.querySelector(':scope > legend')?.textContent?.trim().toLowerCase();
      if (optionId.endsWith('-finish') || legendText === 'finish') initFinishPicker(fieldset);
    });
  }

  function init(root = document) {
    document.querySelectorAll('[data-sunfade-product-story]').forEach((story) => story.remove());
    initGallery(root);
    initFinishOptions(root);
    initProductInformation(root);
  }

  // This asset is loaded with defer, so the product DOM is already parsed here.
  // Initializing immediately avoids waiting behind unrelated DOMContentLoaded work.
  init();

  let reinitializeFrame;
  const productRerenderObserver = new MutationObserver((mutations) => {
    const touchesProduct = mutations.some((mutation) => {
      const target = mutation.target.nodeType === Node.ELEMENT_NODE ? mutation.target : mutation.target.parentElement;
      if (target?.closest?.('.product-information__grid')) return true;

      return [...mutation.addedNodes].some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.('.product-information__grid') || node.querySelector?.('.product-information__grid')),
      );
    });

    if (!touchesProduct) return;

    document.documentElement.classList.remove('sunfade-product-ready');
    document.documentElement.classList.add('sunfade-product-pending');
    document.documentElement.classList.add('sunfade-product-enhancing');

    if (reinitializeFrame) cancelAnimationFrame(reinitializeFrame);
    reinitializeFrame = requestAnimationFrame(() => {
      reinitializeFrame = undefined;
      init(document);
    });
  });

  productRerenderObserver.observe(document.body, { childList: true, subtree: true });

  window.addEventListener('resize', () => productGrids().forEach(scheduleFrameSize), { passive: true });
  window.addEventListener('pageshow', () => productGrids().forEach(scheduleFrameSize));
  desktopLayout.addEventListener?.('change', () => productGrids().forEach(scheduleFrameSize));
  document.fonts?.ready.then(() => productGrids().forEach(scheduleFrameSize));
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
